import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { Product, productService } from '../services/productService';
import { XMarkIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useZebraPrinter } from '../hooks/useZebraPrinter';
import toast from 'react-hot-toast';
import { generateProductLabelZPL } from '../utils/zplUtils';

interface QRPreviewModalProps {
    product: Product;
    noteNumber?: string | null;
    onClose: () => void;
    onPrint?: () => void;
}

export const QRPreviewModal: React.FC<QRPreviewModalProps> = ({ product, noteNumber, onClose }) => {
    const [qrDataUrl, setQrDataUrl] = useState<string>('');
    const [availableSizes, setAvailableSizes] = useState<string[]>([]);
    const [availableColors, setAvailableColors] = useState<string[]>([]);

    const { 
        printLabel, 
        loading: printing,
        isUSBConnected,
        connectUSB,
        usbDeviceName,
        error: printerError
    } = useZebraPrinter();

    // Fetch Variants and Generate QR
    useEffect(() => {
        const loadData = async () => {
            // 1. Generate QR Data URL for Preview
            let qrValue = product.qr_code_value;
            if (!qrValue) {
                const sanitize = (str: string | null | undefined) => str ? str.toUpperCase().replace(/[^A-Z0-9]/g, '') : '';
                const p1 = sanitize(product.cod_producto);
                const p2 = sanitize(product.color || '');
                let p3 = sanitize(product.codigo_proveedor || '');
                if (!p3 && product.marca) {
                    p3 = sanitize(product.marca.substring(0, 3));
                }
                if (!p3) p3 = 'GEN';
                const p4 = sanitize(product.talla || '');
                qrValue = `${p1}-${p2}-${p3}-${p4}`;
            }

            if (qrValue) {
                try {
                    const url = await QRCode.toDataURL(qrValue, { width: 200, margin: 1 });
                    setQrDataUrl(url);
                } catch (err) {
                    console.error(err);
                }
            }

            // 2. Fetch Availability (Sizes/Colors for this Model)
            if (product.cod_producto) {
                const { sizes, colors } = await productService.getVariants(product.cod_producto, product.inventoryType);
                setAvailableSizes(sizes);
                setAvailableColors(colors);
            }
        };

        loadData();
    }, [product]);

    const handlePrint = async () => {
        // Must use the computed qrValue logic inside ZPL utils fallback if missing
        const zpl = generateProductLabelZPL(product, {
            noteNumber,
            availableSizes,
            availableColors
        });

        const success = await printLabel(zpl);
        if (success) {
            toast.success('Enviado a imprimir');
        } else {
            toast.error(printerError || 'No se pudo imprimir. Verifica la conexión USB.');
        }
    };

    // Derived Display Strings
    const dateObj = product.received_at ? new Date(product.received_at) : new Date();
    const dateStr = dateObj.toLocaleDateString('es-MX', {
        timeZone: 'America/Mexico_City',
        day: '2-digit', month: '2-digit', year: '2-digit'
    });

    let sizesStr = '';
    if (availableSizes.length > 0) {
        const numericSizes = availableSizes.map(s => parseFloat(s)).filter(n => !isNaN(n)).sort((a, b) => a - b);
        if (numericSizes.length === availableSizes.length && numericSizes.length > 1) {
            sizesStr = `${numericSizes[0]}-${numericSizes[numericSizes.length - 1]}`;
        } else {
            sizesStr = availableSizes.slice(0, 5).join(' ');
        }
    }

    const colorsStr = availableColors.slice(0, 4).map(c => c.substring(0, 3).toUpperCase()).join(' ');

    const isSold = product.pre_order || !!noteNumber;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900">Vista Previa de Etiqueta (Retail)</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content - Visual Simulation of 4" x 1.2" Label (~800x240 dots) */}
                {/* Scaled to 500px width (10cm). Height ~100px (2cm). Ratio 5:1. */}
                <div className="p-8 bg-gray-100 flex justify-center items-center overflow-x-auto">
                    <div className="bg-white border border-gray-400 w-[500px] h-[100px] relative shadow-sm shrink-0 font-sans text-black select-none">

                        {/* LEFT PANEL (~27%): QR and Date */}
                        {/* LEFT PANEL (~27%): QR and Date */}
                        {/* Scale: 500px / 812 dots = 0.615 px/dot */}
                        {/* ZPL QR: 25,18. 18*0.615 = 11px. Mag 4 (116 dots) * 0.615 = 71px. */}
                        <div className="absolute left-[3%] top-[11px] w-[71px] h-[71px] flex items-start justify-center">
                            {/* Container fixed, alignment top */}
                            {qrDataUrl ? (
                                <img src={qrDataUrl} alt="QR Code" className="w-[71px] h-[71px] object-contain" />
                            ) : (
                                <div className="w-[71px] h-[71px] bg-gray-200 animate-pulse"></div>
                            )}
                        </div>
                        {/* Date: ZPL 25,145 -> 145*0.615 = 89px. Font 15*0.615 = 9px. */}
                        <div className="absolute left-[3%] top-[89px] text-[9px] font-mono whitespace-nowrap leading-none">
                            {dateStr}
                        </div>

                        {/* CENTER PANEL (~40%): Info */}
                        {/* ZPL X=220 -> ~27.5% Left. Y=15 -> ~9px */}
                        <div className="absolute left-[28%] top-[9px] w-[40%] flex flex-col items-start leading-none">
                            {/* REF / PROV CODE - Re-added */}
                            <p className="text-[10px] text-gray-800 font-bold mb-0.5">REF: {product.codigo_proveedor || 'N/A'}</p>

                            {/* MODEL */}
                            <p className={`${product.cod_producto?.length && product.cod_producto.length > 12 ? 'text-[8px]' : 'text-[10px]'} text-gray-600 mb-1 whitespace-nowrap`}>
                                MOD: {product.cod_producto}
                            </p>

                            {/* SIZE (Huge) */}
                            <p className="text-4xl font-extrabold text-black mb-1 tracking-tight">{product.talla}</p>

                            {/* COLOR */}
                            <p className="text-sm font-semibold uppercase mb-3">{product.color}</p>
                        </div>

                        {/* RIGHT PANEL (~33%): Price and Lists */}
                        {/* ZPL X=550 -> ~68% Left. Y variable (min 15) -> ~15 dots -> ~9px */}
                        <div className="absolute left-[68%] top-[15px] w-[30%] flex flex-col items-start h-[90px]">

                            {/* PRICE - HUGE */}
                            <div className="h-[55%] flex items-center justify-start w-full">
                                {isSold ? (
                                    <div className="flex flex-col items-start">
                                        <p className="text-lg font-black text-black leading-none mb-1">{noteNumber || 'VENDIDO'}</p>
                                        <span className="text-[9px] font-bold border border-black px-1">
                                            {(noteNumber && noteNumber.startsWith('TNL-')) ? 'VENTA EN LÍNEA' : 'VENDIDO'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-start leading-none">
                                        {product.rebajado && (
                                            <span className="text-[10px] font-bold text-red-600 mb-0.5 tracking-wider">REBAJADO</span>
                                        )}
                                        <p className={`font-bold tracking-tighter ${product.rebajado ? 'text-3xl' : 'text-4xl'}`}>${product.precio}</p>
                                    </div>
                                )}
                            </div>

                            {/* Divider Line */}
                            <div className="w-full h-px bg-black mb-1"></div>

                            {/* VARIANTS TABLE */}
                            <div className="flex flex-col space-y-0.5 w-full">
                                {sizesStr && <p className="text-[9px] text-gray-900 font-bold truncate w-full">TALLAS: {sizesStr}</p>}
                                {colorsStr && <p className="text-[9px] text-gray-900 font-bold truncate w-full">COLORES: {colorsStr}</p>}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Pre-order Sale Details */}
                {(product.preOrderDetails || (product.pre_order && noteNumber)) && (
                    <div className="px-8 pb-6 bg-gray-50 border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                            <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                            Detalles de Venta (Pre-order)
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">FOLIO</span>
                                <span className="font-bold text-gray-900 text-lg">{product.preOrderDetails?.folio || noteNumber || 'N/A'}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">ESTADO</span>
                                <span className={`font-bold px-2 py-0.5 rounded text-xs inline-block ${product.preOrderDetails?.status === 'COMPLETADA' ? 'bg-green-100 text-green-800' :
                                    product.preOrderDetails?.status === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-800' :
                                        product.preOrderDetails?.status === 'CANCELADA' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                    }`}>
                                    {product.preOrderDetails?.status || 'N/A'}
                                </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">FECHA</span>
                                <span className="font-bold text-gray-900">
                                    {product.preOrderDetails?.date ? new Date(product.preOrderDetails.date).toLocaleString('es-MX', {
                                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    }) : 'N/A'}
                                </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">CLIENTE</span>
                                <span className="font-bold text-gray-900 block break-words leading-tight" title={product.preOrderDetails?.customerName}>{product.preOrderDetails?.customerName || 'N/A'}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">VENDEDOR</span>
                                <span className="font-bold text-gray-900 block break-words leading-tight">{product.preOrderDetails?.sellerName || 'N/A'}</span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="block text-xs text-gray-400 font-medium mb-1">MÉTODO PAGO</span>
                                <span className="font-bold text-gray-900 block">
                                    {product.preOrderDetails?.paymentMethod === 'CASH' ? 'Efectivo' :
                                        product.preOrderDetails?.paymentMethod === 'CARD' ? 'Tarjeta' :
                                            product.preOrderDetails?.paymentMethod === 'TRANSFER' ? 'Transferencia' :
                                                product.preOrderDetails?.paymentMethod || 'N/A'}
                                </span>
                            </div>

                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm col-span-2 md:col-span-3 bg-green-50">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-green-700 font-bold">TOTAL VENTA</span>
                                    <span className="font-bold text-green-700 text-lg">
                                        {product.preOrderDetails?.total ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(product.preOrderDetails.total) : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="p-4 bg-white border-t border-gray-100 flex justify-end gap-2">
                    <button
                        onClick={connectUSB}
                        className={`px-4 py-2 rounded-lg border transition flex items-center gap-2 ${
                            isUSBConnected 
                            ? 'bg-green-50 border-green-500 text-green-700' 
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 font-medium'
                        }`}
                        title={isUSBConnected ? `Conectado a ${usbDeviceName}` : 'Configurar Impresora Zebra via USB'}
                    >
                        <PrinterIcon className="w-5 h-5" />
                        <span>{isUSBConnected ? 'USB Listo' : 'Configurar USB'}</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition"
                    >
                        Cerrar
                    </button>
                    <button
                        onClick={handlePrint}
                        disabled={printing}
                        className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
                    >
                        <PrinterIcon className="w-5 h-5" />
                        {printing ? 'Enviando...' : 'Imprimir'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
